import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { bytesToString } from 'js/lib/FormatUtils';
import { FileWithPreview } from './FileUploadConfiguration.types';
import { FilePreviewContent } from './components/FilePreviewContent';
import { generatePreview, revokeObjectUrl, PreviewResult } from './utils/previewUtils';
import { getFileMetadata } from './utils/fileUtils';
import styles from './FilePreviewModal.module.scss';

type FilePreviewModalProps = {
  file: FileWithPreview;
  open: boolean;
  onClose: () => void;
};

export function FilePreviewModal({ file, open, onClose }: FilePreviewModalProps) {
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const objectUrlRef = useRef<string | null>(null);

  const fileName = file?.name || '';
  const fileType = file?.type || '';
  const metadata = getFileMetadata(fileName, fileType);

  useEffect(() => {
    if (file && open && fileName) {
      setIsLoading(true);

      // Use existing preview if available (for text files)
      if (file.preview && typeof file.preview === 'string') {
        // Check if it's a text-based preview
        const isTextPreview =
          fileType.startsWith('text/') ||
          fileType === 'application/json' ||
          fileName.endsWith('.csv') ||
          fileName.endsWith('.json') ||
          fileName.endsWith('.txt');

        if (isTextPreview) {
          setPreview({
            type: 'text',
            data: file.preview,
            objectUrl: undefined,
          });
          setIsLoading(false);
          return;
        }
      }

      if (file.previewError) {
        setPreview({
          type: 'unsupported',
          data: file.previewError,
          error: file.previewError,
        });
        setIsLoading(false);
        return;
      }

      // Generate preview for the file
      generatePreview(file)
        .then((previewResult) => {
          setPreview(previewResult);
          if (previewResult.objectUrl) {
            objectUrlRef.current = previewResult.objectUrl;
          }
          setIsLoading(false);
        })
        .catch((error) => {
          setPreview({
            type: 'unsupported',
            data: error.message || 'Failed to generate preview',
            error: error.message,
          });
          setIsLoading(false);
        });
    } else if (!open) {
      // Clean up object URLs when modal closes
      if (objectUrlRef.current) {
        revokeObjectUrl(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setPreview(null);
      setIsLoading(true);
    }

    // Cleanup on unmount
    return () => {
      if (objectUrlRef.current) {
        revokeObjectUrl(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [file, open, fileName, fileType]);

  // Don't render if no file
  if (!file) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className={styles.previewDialog}>
      <DialogTitle className={styles.previewTitle}>
        <div className={styles.titleContent}>
          <div className={styles.titleLeft}>
            <div className={styles.fileIconLarge}>{metadata.icon}</div>
            <div className={styles.titleText}>
              <h2 className={styles.dialogTitle}>{fileName}</h2>
              <div className={styles.fileInfo}>
                <span className={`${styles.fileBadge} ${styles.fileTypeBadge}`}>
                  {metadata.label}
                </span>
                <span className={`${styles.fileBadge} ${styles.fileSizeBadge}`}>
                  {bytesToString(file.size || 0)}
                </span>
              </div>
            </div>
          </div>
          <IconButton onClick={onClose} className={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={styles.previewContent}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner} />
            <span className={styles.loadingText}>Loading preview...</span>
          </div>
        ) : preview ? (
          <FilePreviewContent preview={preview} isLoading={isLoading} fileName={fileName} />
        ) : (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner} />
            <span className={styles.loadingText}>Loading preview...</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
